import { AxiosError } from 'axios';

import { axiosInstanceForPublicAPIWithAuth } from '../assets/utils/axiosInstance';
import { IMeetingNote } from '../interfaces/MeetingNotes';

const addQueryParam = (url: string, param: string, value: string | null) => {
  if (value !== null) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${param}=${encodeURIComponent(value)}`;
  }
  return url;
};

export const getMeetingNotesURL = (meetingID: string) =>
  `/schedule/meetings/${meetingID}/notes`;

const meetingService = {
  fetchMeeting: async (
    externalMeetingID: string,
    params?: {
      code: string | null;
      name: string | null;
    },
  ) => {
    try {
      let link = `/schedule/meetings/${externalMeetingID}/join`;
      if (params) {
        link = addQueryParam(link, 'email', params.code);
        link = addQueryParam(link, 'name', params.name);
      }
      const response = await axiosInstanceForPublicAPIWithAuth(
        !(params?.code && params?.name), //invalidate auth when name and code exists
      ).get(link);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  fetchMeetingAttendee: async (
    externalMeetingID: string,
    params?: {
      code: string | null;
      name: string | null;
    },
  ) => {
    try {
      let link = `/schedule/meetings/${externalMeetingID}/attendee`;
      if (params) {
        link = addQueryParam(link, 'email', params.code);
        link = addQueryParam(link, 'name', params.name);
      }
      const response = await axiosInstanceForPublicAPIWithAuth(
        !(params?.code && params?.name), //invalidate auth when name and code exists
      ).get(link);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  endMeeting: async (externalMeetingID: string) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().delete(
        `/schedule/meetings/${externalMeetingID}/end`,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  startRecording: async (
    chimeMeetingID: string,
    reportID: string,
    params?: {
      code: string | null;
      token: string | null;
    },
  ) => {
    try {
      let link = '/schedule/meetings-media-pipeline';
      if (params) {
        link = addQueryParam(link, 'email', params.code);
        link = addQueryParam(link, 'token', params.token);
      }
      const response = await axiosInstanceForPublicAPIWithAuth(
        !params?.code, //invalidate auth when code exists
      ).post(link, {
        meeting_id: chimeMeetingID,
        report_id: reportID,
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  stopRecording: async (
    chimeMeetingID: string,
    mediaPipelineId: string,
    params?: {
      code: string | null;
      token: string | null;
    },
  ) => {
    try {
      let link = '/schedule/meetings-media-pipeline/stop';
      if (params) {
        link = addQueryParam(link, 'email', params.code);
        link = addQueryParam(link, 'token', params.token);
      }
      const response = await axiosInstanceForPublicAPIWithAuth(
        !params?.code, //invalidate auth when code exists
      ).post(link, {
        meeting_id: chimeMeetingID,
        media_pipeline_id: mediaPipelineId,
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  getMeetingNotes: async (meetingID: string): Promise<IMeetingNote[]> => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().get<
        IMeetingNote[]
      >(getMeetingNotesURL(meetingID));
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  createMeetingNote: async (meetingID: number, description: string) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().post(
        `/schedule/meetings/${meetingID}/create-notes`,
        {
          description,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  updateMeetingNote: async (meetingNoteID: string, description: string) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().patch(
        `/schedule/notes/${meetingNoteID}`,
        {
          description,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  deleteMeetingNote: async (meetingNoteID: string) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().delete(
        `/schedule/notes/${meetingNoteID}`,
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  updateMeetingNoteCategory: async (
    meetingNoteID: string,
    categoryID: number | null,
  ) => {
    try {
      const response = await axiosInstanceForPublicAPIWithAuth().patch(
        `/schedule/notes/${meetingNoteID}`,
        {
          category: categoryID,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
};

export default meetingService;
